import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ordersAPI } from '../services/api';
import { orderCustomerListLabel, orderCustomerSummary } from '../utils/orderCustomer';
import './Modal.css';
import './OrderDetails.css';

/** API returns condition as an object; React cannot render objects as text. */
export function conditionLabel(condition) {
  if (!condition) return null;
  if (typeof condition === 'string') return condition;
  return condition.display_name || condition.name || null;
}

export function formatShippingLines(addr) {
  if (!addr) return '';
  const street =
    [addr.address_line1 || addr.street_address, addr.address_line2]
      .filter(Boolean)
      .join(', ') || '';
  const locality = [addr.city, addr.state, addr.postal_code].filter(Boolean).join(', ');
  const country =
    addr.country?.name ?? (typeof addr.country === 'string' ? addr.country : '');
  return [street, locality, country].filter(Boolean).join(' · ');
}

const ORDER_STATUS_BASE = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

function statusOptionsForOrder(currentStatus) {
  if (currentStatus === 'out_for_delivery') {
    const copy = [...ORDER_STATUS_BASE];
    const idx = copy.findIndex((o) => o.value === 'shipped');
    copy.splice(idx + 1, 0, {
      value: 'out_for_delivery',
      label: 'Out for delivery (legacy)',
    });
    return copy;
  }
  return ORDER_STATUS_BASE;
}

function DetailSection({ title, children }) {
  return (
    <div className="order-detail__panel">
      <h3 className="order-detail__section-title">{title}</h3>
      {children}
    </div>
  );
}

function DetailRows({ rows }) {
  return (
    <dl className="order-detail__dl">
      {rows.map(([dt, dd]) => (
        <div key={dt} className="order-detail__row">
          <dt className="order-detail__dt">{dt}</dt>
          <dd className="order-detail__dd">{dd}</dd>
        </div>
      ))}
    </dl>
  );
}

const OrderDetails = ({ order, onUpdate, onDelete, readOnly }) => {
  const [trackingId, setTrackingId] = useState('');
  const [updating, setUpdating] = useState(false);
  const [selectedOrderStatus, setSelectedOrderStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [statusSaving, setStatusSaving] = useState(false);
  const [estimatedDate, setEstimatedDate] = useState('');
  const [estimatedSaving, setEstimatedSaving] = useState(false);
  const [dhlShipping, setDhlShipping] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const statusOptions = useMemo(
    () => statusOptionsForOrder(order?.status),
    [order?.status],
  );

  useEffect(() => {
    setTrackingId(order?.tracking_id || '');
  }, [order?.id, order?.tracking_id]);

  useEffect(() => {
    setSelectedOrderStatus(order?.status || 'pending');
    setStatusNote('');
  }, [order?.id, order?.status]);

  useEffect(() => {
    const ed = order?.estimated_delivery_date;
    if (!ed) {
      setEstimatedDate('');
      return;
    }
    const str = typeof ed === 'string' ? ed : String(ed);
    setEstimatedDate(str.length >= 10 ? str.slice(0, 10) : str);
  }, [order?.id, order?.estimated_delivery_date]);

  if (!order) {
    return null;
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatPrice = (price, currency = order.currency || 'AED') => {
    return `${currency} ${parseFloat(price || 0).toFixed(2)}`;
  };

  const handleUpdateOrderStatus = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!selectedOrderStatus || selectedOrderStatus === order.status) {
      toast.info('Choose a different status to update.');
      return;
    }

    setStatusSaving(true);
    try {
      await ordersAPI.update(order.id, {
        status: selectedOrderStatus,
        description: statusNote.trim() || undefined,
      });
      toast.success('Order status updated.');
      if (onUpdate) onUpdate();
    } catch (error) {
      let errorMessage = 'Error updating status: ';
      if (error.response?.data?.errors) {
        errorMessage += Object.values(error.response.data.errors).flat().join(', ');
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else {
        errorMessage += error.message;
      }
      toast.error(errorMessage);
    } finally {
      setStatusSaving(false);
    }
  };

  const handleSaveEstimatedDelivery = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setEstimatedSaving(true);
    try {
      await ordersAPI.update(order.id, {
        estimated_delivery_date: estimatedDate.trim() || null,
      });
      toast.success('Estimated delivery updated.');
      if (onUpdate) onUpdate();
    } catch (error) {
      let errorMessage = 'Error saving estimated delivery: ';
      if (error.response?.data?.errors) {
        errorMessage += Object.values(error.response.data.errors).flat().join(', ');
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else {
        errorMessage += error.message;
      }
      toast.error(errorMessage);
    } finally {
      setEstimatedSaving(false);
    }
  };

  const handleShipWithDhl = async () => {
    if (!order?.id) return;
    setDhlShipping(true);
    try {
      const response = await ordersAPI.shipWithDhl(order.id);
      const tracking = response.data?.dhl?.tracking_number;
      toast.success(
        tracking
          ? `DHL shipment created. Tracking: ${tracking}`
          : 'DHL shipment created.',
      );
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(
        error.response?.data?.message || error.message || 'DHL shipment failed',
      );
    } finally {
      setDhlShipping(false);
    }
  };

  const handleUpdateTracking = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setUpdating(true);
    try {
      await ordersAPI.update(order.id, {
        tracking_id: trackingId,
      });
      toast.success('Tracking code updated successfully!');
      if (onUpdate) onUpdate();
    } catch (error) {
      let errorMessage = 'Error updating tracking code: ';
      if (error.response?.data?.errors) {
        errorMessage += Object.values(error.response.data.errors).flat().join(', ');
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else {
        errorMessage += error.message;
      }
      toast.error(errorMessage);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (
      !window.confirm(
        'Permanently delete this order? This cannot be undone and removes all related records.',
      )
    ) {
      return;
    }

    setDeleting(true);
    try {
      await ordersAPI.delete(order.id);
      toast.success('Order deleted.');
      if (onDelete) onDelete();
    } catch (error) {
      toast.error(
        error.response?.data?.message || error.message || 'Could not delete order.',
      );
    } finally {
      setDeleting(false);
    }
  };

  const orderInfoRows = [
    ['Order number', order.order_number],
    [
      'Status',
      <span key="st" className={`status-badge status-${order.status}`}>
        {order.status}
      </span>,
    ],
    [
      'Payment status',
      <span key="pay" className={`status-badge status-${order.payment_status}`}>
        {order.payment_status}
      </span>,
    ],
    ['Quantity', order.quantity],
    ['Unit price', formatPrice(order.unit_price)],
    ['Shipping fee', formatPrice(order.shipping_fee ?? 0)],
    ['Total price', formatPrice(order.total_price)],
    ['Currency', order.currency || 'AED'],
  ];

  const datesRows = [
    ['Order date', formatDate(order.created_at)],
    ...(order.shipped_at ? [['Shipped at', formatDate(order.shipped_at)]] : []),
    ...(order.delivered_at ? [['Delivered at', formatDate(order.delivered_at)]] : []),
    ...(order.estimated_delivery_date
      ? [['Estimated delivery', formatDate(order.estimated_delivery_date)]]
      : []),
    ['Tracking ID', order.tracking_id || 'N/A'],
    ...(order.carrier ? [['Carrier', order.carrier.toUpperCase()]] : []),
  ];

  const customer = orderCustomerSummary(order);
  const isGuestCustomer = customer.type === 'guest';

  const buyerRows = [
    ['Account type', isGuestCustomer ? 'Guest checkout' : 'Registered account'],
    ['Email', customer.email || 'N/A'],
    ['Name', customer.name || 'N/A'],
    ['Phone', customer.phone || 'N/A'],
  ];

  const sellerRows = [
    ['Email', order.seller?.email || 'N/A'],
    ['Name', `${order.seller?.first_name || ''} ${order.seller?.last_name || ''}`.trim() || 'N/A'],
    ['Phone', order.seller?.phone_number || 'N/A'],
  ];

  return (
    <div className="admin-page-panel modal-content large order-detail">
      <div className="modal-body" style={{ maxHeight: 'none', overflowY: 'visible' }}>
        <div className="order-detail__grid">
          <DetailSection title="Order">
            <DetailRows rows={orderInfoRows} />
          </DetailSection>
          <DetailSection title="Dates & tracking">
            <DetailRows rows={datesRows} />
          </DetailSection>
        </div>

        {!readOnly && (
          <div className="order-detail__action-card">
            <h3 className="order-detail__card-title">Fulfillment</h3>

            <form onSubmit={handleUpdateOrderStatus} onClick={(e) => e.stopPropagation()}>
              <div className="order-detail__actions-row">
                <div className="order-detail__field-grow">
                  <label className="order-detail__field-label" htmlFor="order-status-select">
                    Status
                  </label>
                  <select
                    id="order-status-select"
                    className="order-detail__select"
                    value={selectedOrderStatus}
                    onChange={(e) => setSelectedOrderStatus(e.target.value)}
                  >
                    {statusOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={statusSaving || selectedOrderStatus === order.status}
                  onClick={(e) => e.stopPropagation()}
                >
                  {statusSaving ? 'Saving…' : 'Apply status'}
                </button>
              </div>
              <label className="order-detail__field-label" htmlFor="order-status-note">
                Internal note (optional)
              </label>
              <textarea
                id="order-status-note"
                className="order-detail__textarea"
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                placeholder="Shown in tracking history for this update"
                rows={2}
              />
              <p className="order-detail__hint">
                Updates status and appends a tracking history entry.
              </p>
            </form>

            <hr style={{ margin: '1.25rem 0', border: 'none', borderTop: '1px solid #e2e8f0' }} />

            <form onSubmit={handleSaveEstimatedDelivery} onClick={(e) => e.stopPropagation()}>
              <label className="order-detail__field-label" htmlFor="order-estimated-delivery">
                Estimated delivery (buyer-facing)
              </label>
              <div className="order-detail__actions-row">
                <div className="order-detail__field-grow">
                  <input
                    id="order-estimated-delivery"
                    type="date"
                    className="order-detail__input"
                    value={estimatedDate}
                    onChange={(e) => setEstimatedDate(e.target.value)}
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-secondary"
                  disabled={estimatedSaving}
                  onClick={(e) => e.stopPropagation()}
                >
                  {estimatedSaving ? 'Saving…' : 'Save date'}
                </button>
              </div>
              <p className="order-detail__hint">Leave empty to clear. Shown in the mobile app.</p>
            </form>

            <hr style={{ margin: '1.25rem 0', border: 'none', borderTop: '1px solid #e2e8f0' }} />

            <div className="order-detail__actions-row" style={{ marginBottom: '1rem' }}>
              <button
                type="button"
                className="btn btn-primary"
                disabled={
                  dhlShipping ||
                  order.carrier === 'dhl' ||
                  !['paid', 'deposit_paid'].includes(order.payment_status)
                }
                onClick={(e) => {
                  e.stopPropagation();
                  void handleShipWithDhl();
                }}
              >
                {dhlShipping
                  ? 'Creating DHL shipment…'
                  : order.carrier === 'dhl' && order.tracking_id
                    ? 'DHL shipped'
                    : 'Ship with DHL'}
              </button>
            </div>
            <p className="order-detail__hint">
              Creates a DHL Express shipment, marks the order as shipped, and saves the tracking number automatically.
            </p>

            <hr style={{ margin: '1.25rem 0', border: 'none', borderTop: '1px solid #e2e8f0' }} />

            <form onSubmit={handleUpdateTracking} onClick={(e) => e.stopPropagation()}>
              <label className="order-detail__field-label" htmlFor="order-tracking-input">
                Carrier tracking code
              </label>
              <div className="order-detail__actions-row">
                <div className="order-detail__field-grow">
                  <input
                    id="order-tracking-input"
                    type="text"
                    className="order-detail__input"
                    value={trackingId}
                    onChange={(e) => setTrackingId(e.target.value)}
                    placeholder="Tracking number"
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updating}
                  onClick={(e) => e.stopPropagation()}
                >
                  {updating ? 'Updating…' : 'Save tracking'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="order-detail__grid">
          <DetailSection title={isGuestCustomer ? 'Customer (guest)' : 'Buyer'}>
            <DetailRows rows={buyerRows} />
          </DetailSection>
          <DetailSection title="Seller">
            <DetailRows rows={sellerRows} />
          </DetailSection>
        </div>

        {order.shippingAddress && (
          <DetailSection title="Shipping address">
            <div className="order-detail__shipping-box">
              <p style={{ margin: 0 }}>{formatShippingLines(order.shippingAddress)}</p>
            </div>
          </DetailSection>
        )}

        {(order.items && order.items.length > 0 ? order.items : order.product ? [{ product: order.product, quantity: order.quantity, unit_price: order.unit_price, subtotal: order.total_price }] : []).map((line, index) => {
          const product = line.product;
          if (!product) return null;
          const conditionTextLine = conditionLabel(product.condition);
          return (
            <DetailSection
              key={line.id || `line-${index}`}
              title={order.items && order.items.length > 1 ? `Product ${index + 1}` : 'Product'}
            >
              <div className="order-detail__product-row">
                {product.images && product.images.length > 0 && (
                  <img
                    className="order-detail__product-thumb"
                    src={product.images[0].image_url}
                    alt={product.title}
                  />
                )}
                <div className="order-detail__product-body">
                  <h4 style={{ margin: '0 0 8px' }}>{product.title}</h4>
                  <p className="order-detail__product-desc">{product.description}</p>
                  <p style={{ margin: '6px 0', fontSize: '0.875rem' }}>
                    <strong>Qty:</strong> {line.quantity ?? 1}
                  </p>
                  <p style={{ margin: '6px 0', fontSize: '0.875rem' }}>
                    <strong>Line total:</strong>{' '}
                    {formatPrice(line.subtotal ?? line.unit_price, product.currency || order.currency || 'AED')}
                  </p>
                  {product.brand && (
                    <p style={{ margin: '6px 0', fontSize: '0.875rem' }}>
                      <strong>Brand:</strong> {product.brand}
                    </p>
                  )}
                  {conditionTextLine ? (
                    <p style={{ margin: '6px 0', fontSize: '0.875rem' }}>
                      <strong>Condition:</strong> {conditionTextLine}
                    </p>
                  ) : null}
                </div>
              </div>
            </DetailSection>
          );
        })}

        {order.tracking && order.tracking.length > 0 && (
          <DetailSection title="Tracking history">
            <div className="order-detail__tracking-list">
              {order.tracking.map((track) => (
                <div
                  key={track.id || `${track.status}-${track.timestamp}`}
                  className="order-detail__tracking-item"
                >
                  <div className="order-detail__tracking-status">{track.status}</div>
                  {track.description && (
                    <div className="order-detail__tracking-desc">{track.description}</div>
                  )}
                  <div className="order-detail__tracking-time">{formatDate(track.timestamp)}</div>
                </div>
              ))}
            </div>
          </DetailSection>
        )}

        {!readOnly ? (
          <div className="order-detail__action-card" style={{ marginTop: '1.5rem' }}>
            <h3 className="order-detail__card-title">Danger zone</h3>
            <p className="order-detail__hint">
              Permanently removes this order, line items, and tracking history.
            </p>
            <button
              type="button"
              className="btn btn-danger"
              disabled={deleting}
              onClick={(e) => {
                e.stopPropagation();
                void handleDeleteOrder();
              }}
            >
              {deleting ? 'Deleting…' : 'Delete order'}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default OrderDetails;
